export const STATE_ABBREVIATIONS: Record<string, string> = {
    'Alabama': 'AL',
    'Alaska': 'AK',
    'Arizona': 'AZ',
    'Arkansas': 'AR',
    'California': 'CA',
    'Colorado': 'CO',
    'Connecticut': 'CT',
    'Delaware': 'DE',
    'Florida': 'FL',
    'Georgia': 'GA',
    'Hawaii': 'HI',
    'Idaho': 'ID',
    'Illinois': 'IL',
    'Indiana': 'IN',
    'Iowa': 'IA',
    'Kansas': 'KS',
    'Kentucky': 'KY',
    'Louisiana': 'LA',
    'Maine': 'ME',
    'Maryland': 'MD',
    'Massachusetts': 'MA',
    'Michigan': 'MI',
    'Minnesota': 'MN',
    'Mississippi': 'MS',
    'Missouri': 'MO',
    'Montana': 'MT',
    'Nebraska': 'NE',
    'Nevada': 'NV',
    'New Hampshire': 'NH',
    'New Jersey': 'NJ',
    'New Mexico': 'NM',
    'New York': 'NY',
    'North Carolina': 'NC',
    'North Dakota': 'ND',
    'Ohio': 'OH',
    'Oklahoma': 'OK',
    'Oregon': 'OR',
    'Pennsylvania': 'PA',
    'Rhode Island': 'RI',
    'South Carolina': 'SC',
    'South Dakota': 'SD',
    'Tennessee': 'TN',
    'Texas': 'TX',
    'Utah': 'UT',
    'Vermont': 'VT',
    'Virginia': 'VA',
    'Washington': 'WA',
    'West Virginia': 'WV',
    'Wisconsin': 'WI',
    'Wyoming': 'WY'
}

export const US_STATES = (
  Object.keys(STATE_ABBREVIATIONS) as string[]
).sort((a, b) => a.localeCompare(b)) as [string, ...string[]]

/** Alphabetical by full state name — standard for generic US state pickers (forms, shipping). */
export const US_STATES_ALPHABETICAL = US_STATES

export const STATE_CODE_TO_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_ABBREVIATIONS).map(([name, code]) => [code, name]),
)

/** Normalize stored state codes (uppercase abbreviations). */
export function normalizeStateCodes(codes: string[]): string[] {
  return [...new Set(codes.map((code) => code.trim().toUpperCase()).filter(Boolean))].sort()
}

/** Display label for a multiselect trigger or summary. */
export function formatStateCodesForDisplay(codes: string[]): string {
  const normalized = normalizeStateCodes(codes)
  if (normalized.length === 0) return 'Select states…'
  const names = normalized
    .map((code) => STATE_CODE_TO_NAME[code] ?? code)
    .sort((a, b) => a.localeCompare(b))
  if (names.length <= 2) return names.join(', ')
  return `${names.length} states selected`
}

/** Comma-separated full state names for read-only display (regional map order). */
export function formatStateCodesAsNames(codes: string[]): string {
  return stateCodesInRegionalDisplayOrder(codes)
    .map((entry) => entry.name)
    .join(', ')
}

export type StateCodeEntry = { code: string; name: string }

export type UsStateRegion = {
  id: string
  label: string
  /** Full state names in conventional US geographic order within the region. */
  stateNames: readonly string[]
}

/** Selected codes in regional display order (east → west across US, geographic within each region). */
export function stateCodesInRegionalDisplayOrder(codes: string[]): StateCodeEntry[] {
  const selected = new Set(normalizeStateCodes(codes))
  const entries: StateCodeEntry[] = []
  for (const region of US_STATE_REGIONS) {
    for (const name of region.stateNames) {
      const code = STATE_ABBREVIATIONS[name]
      if (selected.has(code)) {
        entries.push({ code, name })
      }
    }
  }
  for (const code of normalizeStateCodes(codes)) {
    if (!entries.some((entry) => entry.code === code)) {
      entries.push({ code, name: STATE_CODE_TO_NAME[code] ?? code })
    }
  }
  return entries
}

/**
 * Regional groupings for admin UI (colloquial US zones, not official Census labels).
 * Every state appears exactly once.
 *
 * Order within each region follows familiar US conventions:
 * - Coastlines & belts: north → south (e.g. Maine → Florida, Washington → California)
 * - Interior: east → west or north → south as on a map
 * - Midwest: Great Lakes (east → west) then Plains (north → south), aligned with Census divisions
 * - Non-contiguous Pacific: Alaska, then Hawaii
 *
 * Region sequence is roughly east → west across the country.
 */
export const US_STATE_REGIONS: UsStateRegion[] = [
  {
    id: 'east-coast-northeast',
    label: 'East Coast & Northeast',
    // Atlantic seaboard north → south; Mid-Atlantic after New England
    stateNames: [
      'Maine',
      'New Hampshire',
      'Vermont',
      'Massachusetts',
      'Rhode Island',
      'Connecticut',
      'New York',
      'New Jersey',
      'Pennsylvania',
      'Delaware',
      'Maryland',
      'Virginia',
      'North Carolina',
      'South Carolina',
      'Georgia',
      'Florida',
    ],
  },
  {
    id: 'south-gulf',
    label: 'South & Gulf',
    // Upper South north → south, then Gulf / Deep South
    stateNames: [
      'West Virginia',
      'Kentucky',
      'Tennessee',
      'Arkansas',
      'Mississippi',
      'Alabama',
      'Louisiana',
    ],
  },
  {
    id: 'midwest',
    label: 'Midwest',
    // East North Central (Great Lakes, east → west) then West North Central (Plains, north → south)
    stateNames: [
      'Ohio',
      'Indiana',
      'Illinois',
      'Michigan',
      'Wisconsin',
      'Minnesota',
      'North Dakota',
      'South Dakota',
      'Nebraska',
      'Iowa',
      'Missouri',
      'Kansas',
    ],
  },
  {
    id: 'southwest',
    label: 'Southwest',
    // East → west
    stateNames: ['Oklahoma', 'Texas', 'New Mexico', 'Arizona'],
  },
  {
    id: 'mountain-west',
    label: 'Mountain West',
    // North → south along the Rockies
    stateNames: ['Montana', 'Idaho', 'Wyoming', 'Colorado', 'Utah', 'Nevada'],
  },
  {
    id: 'west-coast-pacific',
    label: 'West Coast & Pacific',
    // Pacific coast north → south, then non-contiguous
    stateNames: ['Washington', 'Oregon', 'California', 'Alaska', 'Hawaii'],
  },
]

/** Flat list in regional display order (not alphabetical). */
export function stateCodesToSortedEntries(codes: string[]): StateCodeEntry[] {
  return stateCodesInRegionalDisplayOrder(codes)
}

/** Selected states grouped by US region (regions with no selection omitted). */
export function groupStateCodesByRegion(
  codes: string[],
): Array<{ region: UsStateRegion; states: StateCodeEntry[] }> {
  const selected = new Set(normalizeStateCodes(codes))
  return US_STATE_REGIONS.map((region) => ({
    region,
    states: region.stateNames
      .map((name) => ({
        code: STATE_ABBREVIATIONS[name],
        name,
      }))
      .filter((entry) => selected.has(entry.code)),
  })).filter((group) => group.states.length > 0)
}